import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import SuccessModal from "../../components/ui/SuccessModal";
import ConfirmModal from "../../components/ui/ConfirmModal";

const Admin = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [activeTab, setActiveTab] = useState("files");
  const [events, setEvents] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [tenders, setTenders] = useState([]);
  const [careers, setCareers] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(false);

  // Success modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [successTitle, setSuccessTitle] = useState("Success!");
  const [successRedirectUrl, setSuccessRedirectUrl] = useState(null);

  // Confirm modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmTitle, setConfirmTitle] = useState("Confirm Action");

  // Calendar form state
  const [eventForm, setEventForm] = useState({
    title: "",
    description: "",
    date: "",
    time: "",
    type: "",
    location: ""
  });

  // File upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadCategory, setUploadCategory] = useState("general");
  const [fileTitle, setFileTitle] = useState("");
  const [fileDescription, setFileDescription] = useState("");

  // Tender form state
  const [tenderForm, setTenderForm] = useState({
    tenderId: "",
    title: "",
    description: "",
    status: "open",
    publishedDate: "",
    deadline: "",
    estimatedValue: ""
  });
  const [selectedTenderFile, setSelectedTenderFile] = useState(null);

  // Career form state
  const [careerForm, setCareerForm] = useState({
    title: "",
    description: "",
    location: "",
    type: "",
    department: "",
    postedDate: "",
    applicationDeadline: "",
    requirements: "",
    salary: "",
    contactEmail: ""
  });
  const [selectedCareerFile, setSelectedCareerFile] = useState(null);

  // Gallery form state
  const [galleryForm, setGalleryForm] = useState({
    title: "",
    description: "",
    category: "",
    date: ""
  });
  const [selectedGalleryFile, setSelectedGalleryFile] = useState(null);

  const API_BASE = import.meta.env.VITE_API_BASE || (import.meta.env.PROD ? "https://karumotti.ac.ke" : "http://localhost:5000");

  // Check if already logged in and validate token
  useEffect(() => {
    const token = sessionStorage.getItem("workshopToken");
    if (token) {
      // Validate token by making a request to a protected endpoint
      fetch(`${API_BASE}/api/auth/validate`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => {
        if (response.ok) {
          setIsLoggedIn(true);
          fetchEvents();
          fetchUploadedFiles();
          fetchTenders();
          fetchCareers();
          fetchGallery();
        } else {
          // Token is invalid, clear it
          sessionStorage.removeItem("workshopToken");
          sessionStorage.removeItem("workshopUser");
        }
      })
      .catch(error => {
        console.error("Token validation failed:", error);
        sessionStorage.removeItem("workshopToken");
        sessionStorage.removeItem("workshopUser");
      });
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");

    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (response.ok) {
        setIsLoggedIn(true);
      sessionStorage.setItem("workshopToken", data.token);
      sessionStorage.setItem("workshopUser", JSON.stringify(data.user));
        fetchEvents();
        fetchUploadedFiles();
        fetchTenders();
        fetchCareers();
        fetchGallery();
      } else {
        setLoginError(data.error || "Workshop access failed");
      }
    } catch (error) {
      setLoginError("Login failed. Please try again.");
    }
  };

  const handleLogout = () => {
    setConfirmTitle("Confirm Logout");
    setConfirmMessage("Are you sure you want to logout of the workshop dashboard?");
    setConfirmAction(() => () => {
      setIsLoggedIn(false);
      setUsername("");
      setPassword("");
      setLoginError("");
      setEvents([]);
      setUploadedFiles([]);
      setTenders([]);
      setCareers([]);
      setGallery([]);
      setEventForm({
        title: "",
        description: "",
        date: "",
        time: "",
        type: "",
        location: ""
      });
      setSelectedFile(null);
      setUploadCategory("general");
      setFileTitle("");
      setFileDescription("");
      setTenderForm({
        tenderId: "",
        title: "",
        description: "",
        status: "open",
        publishedDate: "",
        deadline: "",
        estimatedValue: ""
      });
      setSelectedTenderFile(null);
      setCareerForm({
        title: "",
        description: "",
        location: "",
        type: "",
        department: "",
        postedDate: "",
        applicationDeadline: "",
        requirements: "",
        salary: "",
        contactEmail: ""
      });
      setSelectedCareerFile(null);
      setGalleryForm({
        title: "",
        description: "",
        category: "",
        date: ""
      });
      setSelectedGalleryFile(null);
      sessionStorage.removeItem("workshopToken");
      sessionStorage.removeItem("workshopUser");

      // Show success modal and redirect to homepage
      setSuccessTitle("Logged Out Successfully!");
      setSuccessMessage("You have been logged out of the workshop dashboard. Redirecting to homepage...");
      setSuccessRedirectUrl("/");
      setShowSuccessModal(true);
    });
    setShowConfirmModal(true);
  };

  const fetchEvents = async () => {
    try {
      console.log("Fetching events from:", `${API_BASE}/api/academic-events`);
      const response = await fetch(`${API_BASE}/api/academic-events`);
      console.log("Fetch events response status:", response.status);
      if (response.ok) {
        const data = await response.json();
        console.log("Fetched events:", data);
        setEvents(data);
      } else {
        console.error("Error fetching events:", response.status);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };

  const fetchUploadedFiles = async () => {
    try {
      console.log("Fetching uploaded files from:", `${API_BASE}/api/uploaded-files`);
      const response = await fetch(`${API_BASE}/api/uploaded-files`);
      console.log("Fetch files response status:", response.status);
      if (response.ok) {
        const data = await response.json();
        console.log("Fetched files:", data);
        setUploadedFiles(data);
      } else {
        console.error("Error fetching files:", response.status);
      }
    } catch (error) {
      console.error("Error fetching files:", error);
    }
  };

  const fetchTenders = async () => {
    try {
      console.log("Fetching tenders from:", `${API_BASE}/api/tenders`);
      const response = await fetch(`${API_BASE}/api/tenders`);
      console.log("Fetch tenders response status:", response.status);
      if (response.ok) {
        const data = await response.json();
        console.log("Fetched tenders:", data);
        setTenders(data);
      } else {
        console.error("Error fetching tenders:", response.status);
      }
    } catch (error) {
      console.error("Error fetching tenders:", error);
    }
  };

  const fetchCareers = async () => {
    try {
      console.log("Fetching careers from:", `${API_BASE}/api/careers`);
      const response = await fetch(`${API_BASE}/api/careers`);
      console.log("Fetch careers response status:", response.status);
      if (response.ok) {
        const data = await response.json();
        console.log("Fetched careers:", data);
        setCareers(data);
      } else {
        console.error("Error fetching careers:", response.status);
      }
    } catch (error) {
      console.error("Error fetching careers:", error);
    }
  };

  const fetchGallery = async () => {
    try {
      console.log("Fetching gallery from:", `${API_BASE}/api/gallery`);
      const response = await fetch(`${API_BASE}/api/gallery`);
      console.log("Fetch gallery response status:", response.status);
      if (response.ok) {
        const data = await response.json();
        console.log("Fetched gallery:", data);
        setGallery(data);
      } else {
        console.error("Error fetching gallery:", response.status);
      }
    } catch (error) {
      console.error("Error fetching gallery:", error);
    }
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = sessionStorage.getItem("workshopToken");
      console.log("Submitting event:", eventForm);
      console.log("Token:", token);

      const response = await fetch(`${API_BASE}/api/academic-events`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(eventForm)
      });

      console.log("Response status:", response.status);
      const responseData = await response.json();
      console.log("Response data:", responseData);

      if (response.ok) {
        setEventForm({
          title: "",
          description: "",
          date: "",
          time: "",
          type: "",
          location: ""
        });
        fetchEvents();
        setSuccessTitle("Event Added!");
        setSuccessMessage("The academic event has been successfully added to the calendar.");
        setShowSuccessModal(true);
      } else {
        alert(`Error creating event: ${responseData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Error:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      alert("Please select a file to upload");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("category", uploadCategory);
    formData.append("title", fileTitle);
    formData.append("description", fileDescription);

    try {
      const token = sessionStorage.getItem("workshopToken");
      console.log("Uploading file:", selectedFile.name, "Category:", uploadCategory);
      console.log("Token:", token);

      const response = await fetch(`${API_BASE}/api/upload`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`
        },
        body: formData
      });

      console.log("Upload response status:", response.status);
      const responseData = await response.json();
      console.log("Upload response data:", responseData);

      if (response.ok) {
        setSelectedFile(null);
        setUploadCategory("general");
        setFileTitle("");
        setFileDescription("");
        fetchUploadedFiles();
        setSuccessTitle("File Uploaded!");
        setSuccessMessage("The document has been successfully uploaded and is now available in the resources section.");
        setShowSuccessModal(true);
      } else {
        alert(`Error uploading file: ${responseData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Error:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteEvent = async (id) => {
    setConfirmTitle("Delete Event");
    setConfirmMessage("Are you sure you want to delete this academic event? This action cannot be undone.");
    setConfirmAction(() => async () => {
      try {
        const token = sessionStorage.getItem("workshopToken");
        const response = await fetch(`${API_BASE}/api/academic-events/${id}`, {
          method: "DELETE",
          headers: {
            "Authorization": `Bearer ${token}`
          }
        });

        if (response.ok) {
          fetchEvents();
          setSuccessTitle("Event Deleted!");
          setSuccessMessage("The academic event has been successfully removed from the calendar.");
          setShowSuccessModal(true);
        }
      } catch (error) {
        console.error("Error deleting event:", error);
      }
    });
    setShowConfirmModal(true);
  };

  const handleTenderSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = sessionStorage.getItem("workshopToken");
      console.log("Submitting tender:", tenderForm);
      console.log("Token:", token);

      const formData = new FormData();
      formData.append("tenderId", tenderForm.tenderId);
      formData.append("title", tenderForm.title);
      formData.append("description", tenderForm.description);
      formData.append("status", tenderForm.status);
      formData.append("publishedDate", tenderForm.publishedDate);
      formData.append("deadline", tenderForm.deadline);
      formData.append("estimatedValue", tenderForm.estimatedValue);
      if (selectedTenderFile) {
        formData.append("file", selectedTenderFile);
      }

      const response = await fetch(`${API_BASE}/api/tenders`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`
        },
        body: formData
      });

      console.log("Response status:", response.status);
      const responseData = await response.json();
      console.log("Response data:", responseData);

      if (response.ok) {
        setTenderForm({
          tenderId: "",
          title: "",
          description: "",
          status: "open",
          publishedDate: "",
          deadline: "",
          estimatedValue: ""
        });
        setSelectedTenderFile(null);
        fetchTenders();
        setSuccessTitle("Tender Added!");
        setSuccessMessage("The tender has been successfully published and is now visible to potential bidders.");
        setShowSuccessModal(true);
      } else {
        alert(`Error creating tender: ${responseData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Error:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCareerSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = sessionStorage.getItem("workshopToken");
      console.log("Submitting career:", careerForm);
      console.log("Token:", token);

      const formData = new FormData();
      formData.append("title", careerForm.title);
      formData.append("description", careerForm.description);
      formData.append("location", careerForm.location);
      formData.append("type", careerForm.type);
      formData.append("department", careerForm.department);
      formData.append("postedDate", careerForm.postedDate);
      formData.append("applicationDeadline", careerForm.applicationDeadline);
      formData.append("requirements", careerForm.requirements);
      formData.append("salary", careerForm.salary);
      formData.append("contactEmail", careerForm.contactEmail);
      if (selectedCareerFile) {
        formData.append("file", selectedCareerFile);
      }

      const response = await fetch(`${API_BASE}/api/careers`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`
        },
        body: formData
      });

      console.log("Response status:", response.status);
      const responseData = await response.json();
      console.log("Response data:", responseData);

      if (response.ok) {
        setCareerForm({
          title: "",
          description: "",
          location: "",
          type: "",
          department: "",
          postedDate: "",
          applicationDeadline: "",
          requirements: "",
          salary: "",
          contactEmail: ""
        });
        setSelectedCareerFile(null);
        fetchCareers();
        setSuccessTitle("Career Added!");
        setSuccessMessage("The job posting has been successfully published and is now visible to potential candidates.");
        setShowSuccessModal(true);
      } else {
        alert(`Error creating career: ${responseData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Error:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    e.preventDefault();
    if (!selectedGalleryFile) {
      alert("Please select a file to upload");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("file", selectedGalleryFile);
    formData.append("title", galleryForm.title);
    formData.append("description", galleryForm.description);
    formData.append("category", galleryForm.category);
    formData.append("date", galleryForm.date);

    try {
      const token = sessionStorage.getItem("workshopToken");
      console.log("Uploading gallery file:", selectedGalleryFile.name);
      console.log("Token:", token);

      const response = await fetch(`${API_BASE}/api/gallery`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`
        },
        body: formData
      });

      console.log("Upload response status:", response.status);
      const responseData = await response.json();
      console.log("Upload response data:", responseData);

      if (response.ok) {
        setSelectedGalleryFile(null);
        setGalleryForm({
          title: "",
          description: "",
          category: "",
          date: ""
        });
        fetchGallery();
        setSuccessTitle("Gallery Item Uploaded!");
        setSuccessMessage("The gallery item has been successfully uploaded and is now displayed in the gallery section.");
        setShowSuccessModal(true);
      } else {
        alert(`Error uploading gallery item: ${responseData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Error:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteTender = async (id) => {
    setConfirmTitle("Delete Tender");
    setConfirmMessage("Are you sure you want to delete this tender? This action cannot be undone and will remove all associated documents.");
    setConfirmAction(() => async () => {
      try {
        const token = sessionStorage.getItem("workshopToken");
        const response = await fetch(`${API_BASE}/api/tenders/${id}`, {
          method: "DELETE",
          headers: {
            "Authorization": `Bearer ${token}`
          }
        });

        if (response.ok) {
          fetchTenders();
          setSuccessTitle("Tender Deleted!");
          setSuccessMessage("The tender has been successfully removed from the system.");
          setShowSuccessModal(true);
        }
      } catch (error) {
        console.error("Error deleting tender:", error);
      }
    });
    setShowConfirmModal(true);
  };

  const deleteCareer = async (id) => {
    setConfirmTitle("Delete Career");
    setConfirmMessage("Are you sure you want to delete this job posting? This action cannot be undone and will remove all associated documents.");
    setConfirmAction(() => async () => {
      try {
        const token = sessionStorage.getItem("workshopToken");
        const response = await fetch(`${API_BASE}/api/careers/${id}`, {
          method: "DELETE",
          headers: {
            "Authorization": `Bearer ${token}`
          }
        });

        if (response.ok) {
          fetchCareers();
          setSuccessTitle("Career Deleted!");
          setSuccessMessage("The job posting has been successfully removed from the careers section.");
          setShowSuccessModal(true);
        }
      } catch (error) {
        console.error("Error deleting career:", error);
      }
    });
    setShowConfirmModal(true);
  };

  const deleteGalleryItem = async (id) => {
    setConfirmTitle("Delete Gallery Item");
    setConfirmMessage("Are you sure you want to delete this gallery item? This action cannot be undone and will permanently remove the image from the gallery.");
    setConfirmAction(() => async () => {
      try {
        const token = sessionStorage.getItem("workshopToken");
        const response = await fetch(`${API_BASE}/api/gallery/${id}`, {
          method: "DELETE",
          headers: {
            "Authorization": `Bearer ${token}`
          }
        });

        if (response.ok) {
          fetchGallery();
          setSuccessTitle("Gallery Item Deleted!");
          setSuccessMessage("The gallery item has been successfully removed from the gallery.");
          setShowSuccessModal(true);
        }
      } catch (error) {
        console.error("Error deleting gallery item:", error);
      }
    });
    setShowConfirmModal(true);
  };

  const deleteFile = async (id) => {
    setConfirmTitle("Delete File");
    setConfirmMessage("Are you sure you want to delete this file? This action cannot be undone and will permanently remove the file from the system.");
    setConfirmAction(() => async () => {
      try {
        const token = sessionStorage.getItem("workshopToken");
        const response = await fetch(`${API_BASE}/api/uploaded-files/${id}`, {
          method: "DELETE",
          headers: {
            "Authorization": `Bearer ${token}`
          }
        });

        if (response.ok) {
          fetchUploadedFiles();
          setSuccessTitle("File Deleted!");
          setSuccessMessage("The file has been successfully removed from the system.");
          setShowSuccessModal(true);
        }
      } catch (error) {
        console.error("Error deleting file:", error);
      }
    });
    setShowConfirmModal(true);
  };

  if (!isLoggedIn) {
    return (
      <>
        <Helmet>
          <title>Workshop Access - KTTI</title>
        </Helmet>
        <div className="min-h-screen bg-blue-100 flex items-center justify-center">
          <div className="max-w-md w-full mx-4">
            <div className="bg-card rounded-lg border border-border p-8">
              <div className="text-center mb-6">
                <img src="/kttilogo.png" alt="KTTI Logo" className="mx-auto mb-4 h-12 w-auto" />
                <h1 className="text-2xl font-bold text-foreground">Workshop Access</h1>
                <p className="text-muted-foreground">Access the workshop dashboard</p>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <Input
                  type="text"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                {loginError && (
                  <p className="text-red-500 text-sm">{loginError}</p>
                )}
                <Button type="submit" className="w-full">
                  Login
                </Button>
              </form>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
        <Helmet>
          <title>Workshop Dashboard - KTTI</title>
        </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <br/>
        <br/>

        <div className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-3xl font-bold text-foreground">Workshop Dashboard</h1>
            
            </div>

            {/* Tabs */}
            <div className="flex space-x-1 mb-8 bg-muted p-1 rounded-lg">
              {[
                { id: "files", name: "Documents", icon: "Upload" },
                { id: "tenders", name: "Open Tenders", icon: "Briefcase" },
                { id: "careers", name: "Careers", icon: "Briefcase" },
                { id: "gallery", name: "Gallery", icon: "Image" },
                { id: "calendar", name: "Calendar Events", icon: "Calendar" }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon name={tab.icon} size={16} />
                  <span>{tab.name}</span>
                </button>
              ))}

               <Button variant="outline" onClick={handleLogout}>
                Logout
              </Button>
            </div>

            {/* Calendar Events Tab */}
            {activeTab === "calendar" && (
              <div className="grid lg:grid-cols-2 gap-8">
                {/* Add Event Form */}
                <div className="bg-card rounded-lg border border-border p-6">
                  <h2 className="text-xl font-semibold text-foreground mb-4">Add New Event</h2>
                  <form onSubmit={handleEventSubmit} className="space-y-4">
                    <Input
                      placeholder="Event Title"
                      value={eventForm.title}
                      onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
                      required
                    />
                    <textarea
                      placeholder="Description"
                      value={eventForm.description}
                      onChange={(e) => setEventForm({...eventForm, description: e.target.value})}
                      className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      rows={3}
                    />
                    <Input
                      type="date"
                      value={eventForm.date}
                      onChange={(e) => setEventForm({...eventForm, date: e.target.value})}
                      required
                    />
                    <Input
                      type="time"
                      value={eventForm.time}
                      onChange={(e) => setEventForm({...eventForm, time: e.target.value})}
                    />
                    <Input
                      placeholder="Event Type (e.g., Academic, Exam)"
                      value={eventForm.type}
                      onChange={(e) => setEventForm({...eventForm, type: e.target.value})}
                    />
                    <Input
                      placeholder="Location"
                      value={eventForm.location}
                      onChange={(e) => setEventForm({...eventForm, location: e.target.value})}
                    />
                    <Button type="submit" disabled={loading} className="w-full">
                      {loading ? "Adding..." : "Add Event"}
                    </Button>
                  </form>
                </div>

                {/* Events List */}
                <div className="bg-card rounded-lg border border-border p-6">
                  <h2 className="text-xl font-semibold text-foreground mb-4">Current Events</h2>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {events.map((event) => (
                      <div key={event.id} className="border border-border rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-foreground">{event.title}</h3>
                            <p className="text-sm text-muted-foreground">{event.description}</p>
                            <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
                              <span>{event.date}</span>
                              {event.time && <span>{event.time}</span>}
                              {event.location && <span>{event.location}</span>}
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteEvent(event.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                    {events.length === 0 && (
                      <p className="text-muted-foreground text-center py-8">No events yet</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* File Uploads Tab */}
            {activeTab === "files" && (
              <div className="grid lg:grid-cols-2 gap-8">
                {/* Upload Form */}
                <div className="bg-card rounded-lg border border-border p-6">
                  <h2 className="text-xl font-semibold text-foreground mb-4">Upload Document</h2>
                  <form onSubmit={handleFileUpload} className="space-y-4">
                    <Input
                      placeholder="Document Title (e.g., Service Charter, Fee Structure)"
                      value={fileTitle}
                      onChange={(e) => setFileTitle(e.target.value)}
                      required
                    />
                    <textarea
                      placeholder="Document Description"
                      value={fileDescription}
                      onChange={(e) => setFileDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      rows={2}
                    />
                    <div>
                      <input
                        type="file"
                        onChange={(e) => setSelectedFile(e.target.files[0])}
                        className="w-full"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.mp4,.mp3"
                      />
                    </div>
                    <select
                      value={uploadCategory}
                      onChange={(e) => setUploadCategory(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="documents">Documents</option>
                      <option value="service-charter">Service Charter</option>
                      <option value="fee-structure">Fee Structure</option>
                      <option value="brochure">Brochure</option>
                      <option value="rules-regulations">Rules & Regulations</option>
                      <option value="general">General</option>
                      <option value="academic">Academic</option>
                      <option value="policies">Policies</option>
                    </select>
                    <Button type="submit" disabled={loading || !selectedFile} className="w-full">
                      {loading ? "Uploading..." : "Upload Document"}
                    </Button>
                  </form>
                </div>

                {/* Uploaded Files List */}
                <div className="bg-card rounded-lg border border-border p-6">
                  <h2 className="text-xl font-semibold text-foreground mb-4">Uploaded Documents</h2>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {uploadedFiles.map((file) => (
                      <div key={file.id} className="border border-border rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-foreground">{file.title || file.originalname}</h3>
                            {file.description && <p className="text-sm text-muted-foreground">{file.description}</p>}
                            <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
                              <span className="capitalize">{file.category.replace('-', ' ')}</span>
                              <span>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                              <span>{new Date(file.uploadedAt).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <a
                            href={`${API_BASE}/uploads/${file.filename}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline text-sm"
                            >
                              Download
                            </a>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => deleteFile(file.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {uploadedFiles.length === 0 && (
                      <p className="text-muted-foreground text-center py-8">No documents uploaded yet</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Tenders Tab */}
            {activeTab === "tenders" && (
              <div className="grid lg:grid-cols-2 gap-8">
                {/* Add Tender Form */}
                <div className="bg-card rounded-lg border border-border p-6">
                  <h2 className="text-xl font-semibold text-foreground mb-4">Add New Tender</h2>
                  <form onSubmit={handleTenderSubmit} className="space-y-4">
                    <Input
                      placeholder="Tender ID (e.g., KTTI/2024/001)"
                      value={tenderForm.tenderId}
                      onChange={(e) => setTenderForm({...tenderForm, tenderId: e.target.value})}
                      required
                    />
                    <Input
                      placeholder="Tender Title"
                      value={tenderForm.title}
                      onChange={(e) => setTenderForm({...tenderForm, title: e.target.value})}
                      required
                    />
                    <textarea
                      placeholder="Description"
                      value={tenderForm.description}
                      onChange={(e) => setTenderForm({...tenderForm, description: e.target.value})}
                      className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      rows={3}
                    />
                    <select
                      value={tenderForm.status}
                      onChange={(e) => setTenderForm({...tenderForm, status: e.target.value})}
                      className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="open">Open</option>
                      <option value="closed">Closed</option>
                      <option value="awarded">Awarded</option>
                    </select>
                    <Input
                      type="date"
                      placeholder="Published Date"
                      value={tenderForm.publishedDate}
                      onChange={(e) => setTenderForm({...tenderForm, publishedDate: e.target.value})}
                    />
                    <Input
                      type="date"
                      placeholder="Deadline"
                      value={tenderForm.deadline}
                      onChange={(e) => setTenderForm({...tenderForm, deadline: e.target.value})}
                    />
                    <Input
                      placeholder="Estimated Value"
                      value={tenderForm.estimatedValue}
                      onChange={(e) => setTenderForm({...tenderForm, estimatedValue: e.target.value})}
                    />
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Upload Tender Document (Optional)</label>
                      <input
                        type="file"
                        onChange={(e) => setSelectedTenderFile(e.target.files[0])}
                        className="w-full"
                        accept=".pdf,.doc,.docx"
                      />
                    </div>
                    <Button type="submit" disabled={loading} className="w-full">
                      {loading ? "Adding..." : "Add Tender"}
                    </Button>
                  </form>
                </div>

                {/* Tenders List */}
                <div className="bg-card rounded-lg border border-border p-6">
                  <h2 className="text-xl font-semibold text-foreground mb-4">Current Tenders</h2>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {tenders.map((tender) => (
                      <div key={tender.id} className="border border-border rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-foreground">{tender.title}</h3>
                            <p className="text-sm text-muted-foreground">{tender.tenderId}</p>
                            <p className="text-sm text-muted-foreground">{tender.description}</p>
                            <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
                              <span className={`px-2 py-1 rounded capitalize ${tender.status === 'open' ? 'bg-green-100 text-green-800' : tender.status === 'closed' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                                {tender.status}
                              </span>
                              {tender.deadline && <span>Deadline: {new Date(tender.deadline).toLocaleDateString()}</span>}
                              {tender.estimatedValue && <span>Value: KES {tender.estimatedValue}</span>}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            {tender.filename && (
                              <a
                                href={`${API_BASE}/uploads/${tender.filename}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline text-sm"
                              >
                                Download
                              </a>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => deleteTender(tender.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {tenders.length === 0 && (
                      <p className="text-muted-foreground text-center py-8">No tenders yet</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Careers Tab */}
            {activeTab === "careers" && (
              <div className="grid lg:grid-cols-2 gap-8">
                {/* Add Career Form */}
                <div className="bg-card rounded-lg border border-border p-6">
                  <h2 className="text-xl font-semibold text-foreground mb-4">Add New Career</h2>
                  <form onSubmit={handleCareerSubmit} className="space-y-4">
                    <Input
                      placeholder="Job Title"
                      value={careerForm.title}
                      onChange={(e) => setCareerForm({...careerForm, title: e.target.value})}
                      required
                    />
                    <textarea
                      placeholder="Job Description"
                      value={careerForm.description}
                      onChange={(e) => setCareerForm({...careerForm, description: e.target.value})}
                      className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      rows={3}
                    />
                    <Input
                      placeholder="Location"
                      value={careerForm.location}
                      onChange={(e) => setCareerForm({...careerForm, location: e.target.value})}
                    />
                    <select
                      value={careerForm.type}
                      onChange={(e) => setCareerForm({...careerForm, type: e.target.value})}
                      className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Select Type</option>
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Contract">Contract</option>
                      <option value="Internship">Internship</option>
                    </select>
                    <Input
                      placeholder="Department"
                      value={careerForm.department}
                      onChange={(e) => setCareerForm({...careerForm, department: e.target.value})}
                    />
                    <Input
                      type="date"
                      placeholder="Posted Date"
                      value={careerForm.postedDate}
                      onChange={(e) => setCareerForm({...careerForm, postedDate: e.target.value})}
                    />
                    <Input
                      type="date"
                      placeholder="Application Deadline"
                      value={careerForm.applicationDeadline}
                      onChange={(e) => setCareerForm({...careerForm, applicationDeadline: e.target.value})}
                    />
                    <textarea
                      placeholder="Requirements (one per line)"
                      value={careerForm.requirements}
                      onChange={(e) => setCareerForm({...careerForm, requirements: e.target.value})}
                      className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      rows={3}
                    />
                    <Input
                      placeholder="Salary"
                      value={careerForm.salary}
                      onChange={(e) => setCareerForm({...careerForm, salary: e.target.value})}
                    />
                    <Input
                      type="email"
                      placeholder="Contact Email"
                      value={careerForm.contactEmail}
                      onChange={(e) => setCareerForm({...careerForm, contactEmail: e.target.value})}
                    />
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Upload Job Description Document (Optional)</label>
                      <input
                        type="file"
                        onChange={(e) => setSelectedCareerFile(e.target.files[0])}
                        className="w-full"
                        accept=".pdf,.doc,.docx"
                      />
                    </div>
                    <Button type="submit" disabled={loading} className="w-full">
                      {loading ? "Adding..." : "Add Career"}
                    </Button>
                  </form>
                </div>

                {/* Careers List */}
                <div className="bg-card rounded-lg border border-border p-6">
                  <h2 className="text-xl font-semibold text-foreground mb-4">Current Careers</h2>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {careers.map((career) => (
                      <div key={career.id} className="border border-border rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-foreground">{career.title}</h3>
                            <p className="text-sm text-muted-foreground">{career.location} • {career.type}</p>
                            <p className="text-sm text-muted-foreground">{career.description}</p>
                            <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
                              <span>{career.department}</span>
                              {career.applicationDeadline && <span>Deadline: {new Date(career.applicationDeadline).toLocaleDateString()}</span>}
                              {career.salary && <span>Salary: KES {career.salary}</span>}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            {career.filename && (
                              <a
                                href={`${API_BASE}/uploads/${career.filename}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline text-sm"
                              >
                                Download
                              </a>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => deleteCareer(career.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {careers.length === 0 && (
                      <p className="text-muted-foreground text-center py-8">No careers yet</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Gallery Tab */}
            {activeTab === "gallery" && (
              <div className="grid lg:grid-cols-2 gap-8">
                {/* Upload Gallery Form */}
                <div className="bg-card rounded-lg border border-border p-6">
                  <h2 className="text-xl font-semibold text-foreground mb-4">Upload Gallery Item</h2>
                  <form onSubmit={handleGalleryUpload} className="space-y-4">
                    <div>
                      <input
                        type="file"
                        onChange={(e) => setSelectedGalleryFile(e.target.files[0])}
                        className="w-full"
                        accept="image/*,video/*"
                      />
                    </div>
                    <Input
                      placeholder="Title"
                      value={galleryForm.title}
                      onChange={(e) => setGalleryForm({...galleryForm, title: e.target.value})}
                      required
                    />
                    <textarea
                      placeholder="Description"
                      value={galleryForm.description}
                      onChange={(e) => setGalleryForm({...galleryForm, description: e.target.value})}
                      className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      rows={2}
                    />
                    <Input
                      placeholder="Category (e.g., Events, Activities)"
                      value={galleryForm.category}
                      onChange={(e) => setGalleryForm({...galleryForm, category: e.target.value})}
                    />
                    <Input
                      type="date"
                      placeholder="Date"
                      value={galleryForm.date}
                      onChange={(e) => setGalleryForm({...galleryForm, date: e.target.value})}
                    />
                    <Button type="submit" disabled={loading || !selectedGalleryFile} className="w-full">
                      {loading ? "Uploading..." : "Upload Item"}
                    </Button>
                  </form>
                </div>

                {/* Gallery Items List */}
                <div className="bg-card rounded-lg border border-border p-6">
                  <h2 className="text-xl font-semibold text-foreground mb-4">Gallery Items</h2>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {gallery.map((item) => (
                      <div key={item.id} className="border border-border rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-foreground">{item.title}</h3>
                            <p className="text-sm text-muted-foreground">{item.description}</p>
                            <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
                              <span className="capitalize">{item.category}</span>
                              <span className="capitalize">{item.type}</span>
                              <span>{item.date ? new Date(item.date).toLocaleDateString() : 'No date'}</span>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <a
                              href={`${API_BASE}/uploads/${item.filename}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline text-sm"
                            >
                              View
                            </a>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => deleteGalleryItem(item.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {gallery.length === 0 && (
                      <p className="text-muted-foreground text-center py-8">No gallery items yet</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          setSuccessRedirectUrl(null);
        }}
        title={successTitle}
        message={successMessage}
        autoClose={successTitle === "Logged Out Successfully!"}
        redirectUrl={successRedirectUrl}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmAction}
        title={confirmTitle}
        message={confirmMessage}
        confirmText="Logout"
        type="warning"
      />
    </>
  );
};

export default Admin;
